// Central dataLayer push helper.
//
// window.dataLayer is initialized by the GTM bootstrap. This helper is safe to
// call before that bootstrap: dataLayer is auto-initialized as an array, and
// GTM picks up queued pushes when it loads.
//
// Every event is enriched with first-touch attribution (utm_source, utm_medium,
// utm_campaign, utm_content, utm_term, fbclid, gclid, ttclid, msclkid) from
// utils/attribution — so GTM tags forwarding to Meta / GA4 / Google Ads can
// stamp the ad-click origin on every conversion. The user_data / caller-
// supplied payload always wins over attribution keys (spread order).

import { attributionForCustomData } from './attribution'
import { catalogIdForShopItem } from './catalogId'

export function generateEventId() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID()
  }
  return 'evt-' + Date.now() + '-' + Math.random().toString(36).slice(2, 11)
}

// Read Meta's device-fingerprint (_fbp) and click-ID (_fbc) cookies. The
// pixel base code sets _fbp on load; _fbc is derived from an `fbclid` URL
// param at click time. Both are what Meta's CAPI matcher uses to link a
// server-side event to an ad click. GTM's server-side Meta CAPI tag
// forwards whatever user_data.* fields we push, so surfacing these here
// wires them directly into Meta's match graph — the single biggest lever
// for attribution match-rate.
function readMetaClickIds() {
  if (typeof document === 'undefined') return {}
  const m = document.cookie
  const fbp = m.match(/(?:^|;\s*)_fbp=([^;]+)/)
  const fbc = m.match(/(?:^|;\s*)_fbc=([^;]+)/)
  return {
    ...(fbp ? { fbp: fbp[1] } : {}),
    ...(fbc ? { fbc: fbc[1] } : {}),
  }
}

// Enrich a caller-supplied user_data object with Meta / GA4 matching signals
// GTM tags forward straight through. Never overwrites caller-supplied keys.
function enrichUserData(userData) {
  if (typeof window === 'undefined') return userData
  const enriched = {
    ...(navigator && navigator.userAgent ? { client_user_agent: navigator.userAgent } : {}),
    ...readMetaClickIds(),
    ...(userData || {}),
  }
  return Object.keys(enriched).length ? enriched : undefined
}

export function pushDataLayerEvent(name, payload) {
  if (typeof window === 'undefined' || !name) return
  window.dataLayer = window.dataLayer || []

  const eventId = (payload && payload.event_id) || generateEventId()
  const event = {
    event: name,
    event_id: eventId,
    ...attributionForCustomData(),
    ...(payload || {}),
  }
  event.event_id = eventId

  // Enrich user_data with Meta match signals (fbp/fbc cookies + client UA).
  // Runs AFTER the caller-supplied payload spread so caller-provided user_data
  // fields still win; only the enrichment KEYS are added.
  if (payload && payload.user_data) {
    event.user_data = enrichUserData(payload.user_data)
  }

  try {
    window.dataLayer.push(event)
  } catch (e) {
    // Never break the page on a tracking push.
  }
  return eventId
}

export function pushEcommerceEvent(name, ecommerce, extra) {
  if (typeof window === 'undefined' || !name) return
  window.dataLayer = window.dataLayer || []
  try {
    window.dataLayer.push({ ecommerce: null })
  } catch (e) {}
  return pushDataLayerEvent(name, { ecommerce, ...(extra || {}) })
}

// Convert a VRS shopItem to a GA4 item[] entry.
export function toGA4Item(shopItem, quantity) {
  if (!shopItem) return null
  const p = shopItem.discountPrice || shopItem.price
  const item = {
    // itemType-based catalog id (see utils/catalogId.js), NOT shopItem.id,
    // which is the display name and matches no Meta or Merchant Center item.
    item_id: catalogIdForShopItem(shopItem),
    item_name: shopItem.name,
    quantity: quantity || 1,
    google_business_vertical: 'retail',
  }
  if (p) {
    item.price = p.priceCents / 100
    item.currency = p.currency
  }
  return item
}

// Convert a set of shopItems (an offer / a whole cart) to items[] + currency + value.
// items is [{item, quantity}] pairs; falls back to quantity 1 if only shopItems passed.
export function buildEcommercePayload(items) {
  if (!items || !items.length) {
    return { currency: undefined, value: 0, items: [] }
  }
  const gaItems = items
    .map(entry => {
      if (entry && entry.item) return toGA4Item(entry.item, entry.quantity)
      return toGA4Item(entry, 1)
    })
    .filter(Boolean)
  const currency = gaItems.find(i => i.currency)?.currency
  const value = gaItems.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0)
  return { currency, value, items: gaItems }
}
